import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { StatefulPopover, TRIGGER_TYPE, PLACEMENT } from 'baseui/popover'
import { Delete } from 'baseui/icon'

import IconWarning from '@/shared/icons/NewDesign/IconWarning'
import {
  selectCutOffers,
  selectCutOffersUpdatedAt,
  selectTeamName,
} from '@/containers/Login/AuthSelectors'

// Pretty-print a backend date (ISO string or YYYY-MM-DD) in the agent's
// current locale, short form (e.g. "14 juin", "Jun 14"). Returns '' for
// missing / unparseable values so we can render conditionally.
const formatCutDate = (value, locale) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  try {
    return new Intl.DateTimeFormat(locale || undefined, { day: '2-digit', month: 'short' }).format(date)
  } catch {
    return date.toLocaleDateString()
  }
}

/**
 * Sticky top banner showing which offers are currently in a cut period for
 * the logged-in agent's team. Two parts: localized sentence + popover trigger
 * (hover). The popover shows the styled list of offers.
 *
 * When a fresh broadcast event mutates the cut-offers list, the banner emits
 * a subtle visual signal (glow + "Mis à jour" badge) for ~3 seconds so the
 * agent doesn't miss the change. The signal fires on EVERY signature change,
 * including the first broadcast after login.
 *
 * The banner is dismissible. It re-appears whenever the list changes so a
 * dismissed banner doesn't hide newer cut periods.
 */
const UPDATED_FLASH_MS = 3500

// ── Banner ────────────────────────────────────────────────────────────────
const containerStyle = (flashing) => ({
  position: 'relative',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.6rem',
  padding: '0.6rem 3rem',
  background: 'linear-gradient(180deg, #FFF6DA 0%, #FFE9A6 100%)',
  borderBottom: '1px solid #E5B600',
  color: '#5A4500',
  fontSize: '0.9rem',
  fontWeight: 500,
  letterSpacing: '0.01em',
  textAlign: 'center',
  boxShadow: flashing
    ? '0 0 0 3px rgba(245, 158, 11, 0.45), 0 4px 16px rgba(245, 158, 11, 0.25)'
    : '0 1px 2px rgba(0, 0, 0, 0.04)',
  transition: 'box-shadow 250ms ease-out',
})

const linkButtonStyle = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: '#7A4A00',
  font: 'inherit',
  fontWeight: 700,
  textDecoration: 'underline',
  textUnderlineOffset: '2px',
  padding: 0,
  marginLeft: '0.25rem',
}

const closeButtonStyle = {
  position: 'absolute',
  right: '1rem',
  top: '50%',
  transform: 'translateY(-50%)',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: '#5A4500',
  display: 'inline-flex',
  alignItems: 'center',
  padding: '0.15rem',
  borderRadius: '4px',
}

const updatedBadgeStyle = (visible) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.25rem',
  marginLeft: '0.5rem',
  padding: '0.1rem 0.5rem',
  background: '#F59E0B',
  color: '#FFFFFF',
  borderRadius: '999px',
  fontSize: '0.7rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(-4px)',
  transition: 'opacity 200ms ease-out, transform 200ms ease-out',
  pointerEvents: 'none',
})

const pulseDotStyle = {
  width: '6px',
  height: '6px',
  borderRadius: '50%',
  background: '#FFFFFF',
  animation: 'cutOffersPulse 1s ease-in-out infinite',
}

// ── Popover content ───────────────────────────────────────────────────────
const popoverCardStyle = {
  minWidth: '300px',
  maxWidth: '420px',
  background: '#FFFFFF',
  borderRadius: '8px',
  boxShadow: '0 12px 32px rgba(15, 23, 42, 0.18), 0 2px 6px rgba(15, 23, 42, 0.08)',
  overflow: 'hidden',
  fontFamily: 'inherit',
}

const popoverHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.75rem 1rem',
  background: 'linear-gradient(180deg, #FFF6DA 0%, #FFE9A6 100%)',
  borderBottom: '1px solid #F1D27A',
  fontSize: '0.85rem',
  fontWeight: 700,
  color: '#5A4500',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
}

const popoverListStyle = {
  margin: 0,
  padding: '0.25rem 0',
  maxHeight: '60vh',
  overflowY: 'auto',
  listStyle: 'none',
}

const popoverItemStyle = {
  padding: '0.55rem 1rem',
  fontSize: '0.9rem',
  color: '#1F2937',
  borderTop: '1px solid #F3F4F6',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '0.75rem',
  lineHeight: 1.35,
}

const popoverItemFirstStyle = { ...popoverItemStyle, borderTop: 'none' }

const offerNameStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  minWidth: 0,
}

const offerDatesStyle = {
  fontSize: '0.78rem',
  fontWeight: 600,
  color: '#92400E',
  background: '#FEF3C7',
  padding: '0.15rem 0.5rem',
  borderRadius: '999px',
  whiteSpace: 'nowrap',
  flexShrink: 0,
}

const offerDotStyle = {
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  background: '#F59E0B',
  flexShrink: 0,
}

// Keyframes injected once for the pulsing dot on the "Mis à jour" pill.
// We use a plain <style> element so we don't pull in another styling lib.
const KeyframesStyle = () => (
  <style>{`
    @keyframes cutOffersPulse {
      0%, 100% { transform: scale(1); opacity: 1; }
      50% { transform: scale(1.35); opacity: 0.7; }
    }
  `}</style>
)

const CutOffersBanner = () => {
  const { t, i18n } = useTranslation()
  // Read straight from the Redux store ─ NOT through selectAuthData, which
  // falls back to a stale localStorage snapshot and wouldn't see the
  // broadcast updates.
  const cutOffers = useSelector(selectCutOffers)
  // Timestamp bumped on every broadcast. Used to drive the "just updated"
  // visual signal even when the list content is identical (idempotent
  // refreshes). Watching the list itself would miss those cases.
  const updatedAt = useSelector(selectCutOffersUpdatedAt)
  const teamName = useSelector(selectTeamName)

  // Normalize i18n's compound key (e.g. "frFr") into a BCP-47 locale for
  // Intl.DateTimeFormat. Fallback to the browser locale.
  const locale = useMemo(() => {
    const raw = i18n?.language ?? ''
    // 'frFr' → 'fr-FR'  /  'itIt' → 'it-IT'  /  'beNl' → 'nl-BE' (swap)
    if (raw.length === 4) {
      const lang = raw.slice(0, 2).toLowerCase()
      const region = raw.slice(2).toUpperCase()
      // Dutch in Belgium is 'nl-BE', not 'be-NL'. Swap when both parts differ.
      if (lang === 'be' && region === 'NL') return 'nl-BE'
      return `${lang}-${region}`
    }
    return raw || undefined
  }, [i18n?.language])

  const [dismissedSignature, setDismissedSignature] = useState(null)
  const [isJustUpdated, setIsJustUpdated] = useState(false)
  // Skip the visual signal on the very first render — only flash on actual
  // signature changes happening at runtime (i.e. broadcast events).
  const isFirstRender = useRef(true)

  const offers = Array.isArray(cutOffers) ? cutOffers : []
  const currentSignature = offers
    .map(offer => offer?.id ?? '')
    .sort()
    .join(',')

  // Re-arm the visual signal on EVERY broadcast (tracked via `updatedAt`,
  // bumped by the reducer regardless of list content). Skip the very first
  // render — the initial value of `updatedAt` shouldn't trigger a flash.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return undefined
    }
    if (!updatedAt) return undefined

    // Auto-clear any pending dismissal so a fresh broadcast always
    // re-surfaces the banner.
    setDismissedSignature(null)
    setIsJustUpdated(true)
    const timer = setTimeout(() => setIsJustUpdated(false), UPDATED_FLASH_MS)
    return () => clearTimeout(timer)
  }, [updatedAt])

  if (offers.length === 0) return null
  if (dismissedSignature === currentSignature) return null

  return (
    <>
      <KeyframesStyle />
      <div style={containerStyle(isJustUpdated)} role='status' aria-live='polite'>
        <IconWarning size={18} color='#B65900' />
        <span>
          {t('common:cut_offers.banner', {
            count: offers.length,
            teamName: teamName || '',
          })}
          <StatefulPopover
            triggerType={TRIGGER_TYPE.hover}
            placement={PLACEMENT.bottom}
            showArrow
            accessibilityType='tooltip'
            overrides={{
              Body: { style: { zIndex: 1200 } },
              Inner: { style: { background: 'transparent' } },
            }}
            content={
              <div style={popoverCardStyle}>
                <div style={popoverHeaderStyle}>
                  <IconWarning size={16} color='#B65900' />
                  {t('common:cut_offers.popover_title', { count: offers.length })}
                </div>
                <ul style={popoverListStyle}>
                  {offers.map((offer, index) => {
                    // Accept both camelCase (frontend convention) and the
                    // snake_case shape the backend currently sends.
                    const startRaw = offer.cut_start ?? offer.cutStart
                    const endRaw = offer.cut_end ?? offer.cutEnd
                    const start = formatCutDate(startRaw, locale)
                    const end = formatCutDate(endRaw, locale)
                    // Three cases:
                    //   - start + end   → "14 juin → 16 juin"
                    //   - start only    → "14 juin · pour une durée indéterminée"
                    //                     (cut_end is null = no scheduled end)
                    //   - end only      → "→ 16 juin"  (rare, but tolerated)
                    let dateLabel = ''
                    if (start && end) {
                      dateLabel = `${start} → ${end}`
                    } else if (start) {
                      dateLabel = `${start} · ${t('common:cut_offers.indeterminate_duration')}`
                    } else if (end) {
                      dateLabel = `→ ${end}`
                    }

                    return (
                      <li
                        key={offer.id ?? index}
                        style={index === 0 ? popoverItemFirstStyle : popoverItemStyle}
                      >
                        <span style={offerNameStyle}>
                          <span style={offerDotStyle} aria-hidden='true' />
                          <span>{offer.name ?? `#${offer.id}`}</span>
                        </span>
                        {dateLabel && <span style={offerDatesStyle}>{dateLabel}</span>}
                      </li>
                    )
                  })}
                </ul>
              </div>
            }
          >
            <button type='button' style={linkButtonStyle}>
              {t('common:cut_offers.link')}
            </button>
          </StatefulPopover>
          <span style={updatedBadgeStyle(isJustUpdated)} aria-live='polite'>
            <span style={pulseDotStyle} aria-hidden='true' />
            {t('common:cut_offers.updated_badge')}
          </span>
        </span>

        <button
          type='button'
          onClick={() => setDismissedSignature(currentSignature)}
          style={closeButtonStyle}
          aria-label={t('common:cut_offers.dismiss')}
          title={t('common:cut_offers.dismiss')}
        >
          <Delete size={20} />
        </button>
      </div>
    </>
  )
}

export default CutOffersBanner
